/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { useAppDispatch } from '../../../../../app/hooks';
import { AudienceType, AudiencesType, DocumentVersionType } from '../../../algorithm/types';
import { AudienceFilterProvider, PublishedOnlyProvider } from '../../hooks/ChatContext';
import { useScrollDirection } from '../../useHooks';
import AudienceComponent from './AudienceComponent';
import styles from './AudienceContainer.module.scss';
import { setPublishedOnly as setPublishedOnlyAction } from '../../chatSlice';

type AudienceContainerProps = {
  audienceFilter: AudienceType;
  publishedOnly: boolean;
  chatRef: React.RefObject<HTMLDivElement>;
  fixedAudiences?: AudiencesType;
  documentVersion?: DocumentVersionType;
  onChangeAudience?: (audience: AudienceType) => void;
  showPublishedOnly?: boolean;
  showFeedback?: boolean;
  compact?: boolean;
  hideAudience?: boolean;
  children: React.ReactNode;
};

export default function AudienceContainer({
  audienceFilter,
  publishedOnly,
  chatRef,
  fixedAudiences,
  documentVersion,
  onChangeAudience,
  showPublishedOnly,
  showFeedback,
  compact,
  hideAudience,
  children,
}: AudienceContainerProps) {
  const dispatch = useAppDispatch();

  const scrollDirection = useScrollDirection(chatRef);
  /* This is to auto-hide the audience bar on mobile.
   *  Note this only sets the css class. The actual hiding is done in AudienceContainer.module.scss
   * and only when the screen width and height is smaller than
   * `$width-to-hide-audience-on-scroll` and `$height-to-hide-audience-on-scroll`
   * respectively.
   */
  const scrollHideAudience = scrollDirection === 'down';

  const setPublishedOnly = (value: boolean) => {
    dispatch(setPublishedOnlyAction(value));
  };

  return (
    <AudienceFilterProvider value={audienceFilter}>
      <PublishedOnlyProvider value={publishedOnly}>
        {/* styles.scrollHideAudience only has effect on mobile - it has a media query */}
        {!hideAudience && (
          <div className={`${styles.audienceComponentContainer} ${scrollHideAudience && styles.scrollHideAudience}`}>
            <AudienceComponent
              fixedAudiences={fixedAudiences}
              documentVersion={documentVersion}
              publishedOnly={publishedOnly}
              onChangeAudience={onChangeAudience}
              onSetPublishedOnly={setPublishedOnly}
              showPublishedOnly={showPublishedOnly}
              showFeedback={showFeedback}
              compact={compact}
            />
          </div>
        )}
        {children}
      </PublishedOnlyProvider>
    </AudienceFilterProvider>
  );
}
